import { useMemo, useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChefHat,
  Lightbulb,
  Loader2,
  RefreshCw,
  Rocket,
  X,
} from 'lucide-react';

import { RestaurantOnboardingStatus } from '../../../types/restaurant/RestaurantOnboardingTypes';
import {
  ONBOARDING_STEP_CONTENT,
  ONBOARDING_USAGE_SLIDES,
} from './onboardingContent';

interface Props {
  status: RestaurantOnboardingStatus | null;
  loading: boolean;
  refreshing: boolean;
  onRefresh: () => void;
  onNavigate: (route: string) => void;
  onClose: () => void;
  onFinish: () => void;
}

const RestaurantOnboardingWizard = ({
  status,
  loading,
  refreshing,
  onRefresh,
  onNavigate,
  onClose,
  onFinish,
}: Props) => {
  const steps = status?.steps ?? [];
  const usage = ONBOARDING_USAGE_SLIDES;
  const totalScreens = steps.length + usage.length + 1;

  const [bannerOk, setBannerOk] = useState(true);
  const [index, setIndex] = useState(0);
  const clamped = Math.min(index, Math.max(0, totalScreens - 1));

  const phase: 'setup' | 'usage' | 'done' = useMemo(() => {
    if (clamped < steps.length) return 'setup';
    if (clamped < steps.length + usage.length) return 'usage';
    return 'done';
  }, [clamped, steps.length, usage.length]);

  const goNext = () => setIndex((i) => Math.min(i + 1, totalScreens - 1));
  const goBack = () => setIndex((i) => Math.max(i - 1, 0));

  const setupStep = phase === 'setup' ? steps[clamped] : null;
  const usageSlide =
    phase === 'usage' ? usage[clamped - steps.length] : null;

  const indexedSteps = steps.map((step, index) => ({ step, index }));
  const setupGroups = {
    required: indexedSteps.filter((s) => s.step.required),
    optional: indexedSteps.filter((s) => !s.step.required),
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
      <div className="flex h-full max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-stroke bg-white shadow-2xl dark:border-strokedark dark:bg-boxdark">
        {/* Banner del módulo de restaurante: franja de marca (SVG/CSS, sin peso)
            con la imagen real por encima si existe en public/images/restaurant/. */}
        <div className="relative aspect-[1400/215] w-full shrink-0 overflow-hidden border-b border-stroke bg-[#FCF6EA] dark:border-strokedark">
          <div className="absolute inset-0 flex items-center gap-3.5 bg-gradient-to-r from-primary/10 via-white to-white px-6 dark:via-boxdark dark:to-boxdark">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary text-white">
              <ChefHat className="h-6 w-6" />
            </span>
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-primary">
                Codenova · Restaurantes
              </p>
              <p className="truncate text-base font-extrabold text-black dark:text-white sm:text-lg">
                Todo tu restaurante, en un solo lugar
              </p>
              <p className="hidden truncate text-xs text-bodydark2 sm:block">
                Tecnología que sirve a tu pasión
              </p>
            </div>
          </div>
          {bannerOk && (
            <img
              src="/images/restaurant/onboarding-banner.jpg"
              alt="Codenova para restaurantes"
              onError={() => setBannerOk(false)}
              className="relative h-full w-full object-cover object-bottom"
            />
          )}
        </div>

      <div className="flex min-h-0 flex-1">
        {/* Rail de pasos (escritorio) */}
        <aside className="hidden w-[268px] shrink-0 flex-col border-r border-stroke bg-gray-2/40 dark:border-strokedark dark:bg-meta-4/20 lg:flex">
          <div className="border-b border-stroke px-5 py-4 dark:border-strokedark">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-primary">
              Configuración
            </p>
            <h2 className="mt-0.5 text-sm font-bold text-black dark:text-white">
              Tu restaurante
            </h2>
            {status && (
              <div className="mt-3">
                <div className="flex items-center justify-between text-[11px] font-medium text-bodydark2">
                  <span>
                    {status.completedTotal}/{status.totalSteps} pasos
                  </span>
                  <span>{status.percent}%</span>
                </div>
                <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-stroke dark:bg-strokedark">
                  <div
                    className="h-full rounded-full bg-primary transition-all"
                    style={{ width: `${status.percent}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          <nav className="flex-1 space-y-5 overflow-y-auto px-3 py-4">
            {[
              { label: 'Obligatorios', items: setupGroups.required },
              { label: 'Opcionales', items: setupGroups.optional },
            ]
              .filter((group) => group.items.length > 0)
              .map((group) => (
                <div key={group.label}>
                  <p className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-bodydark2">
                    {group.label}
                  </p>
                  <div className="relative">
                    <span className="absolute bottom-5 left-[21px] top-5 w-px bg-stroke dark:bg-strokedark" />
                    {group.items.map(({ step, index: i }) => {
                      const content = ONBOARDING_STEP_CONTENT[step.key];
                      const active = phase === 'setup' && i === clamped;
                      return (
                        <button
                          key={step.key}
                          type="button"
                          onClick={() => setIndex(i)}
                          className={`relative z-10 flex w-full items-center gap-3 rounded-lg py-1.5 pl-2 pr-2 text-left transition ${
                            active
                              ? 'bg-primary/10'
                              : 'hover:bg-white/70 dark:hover:bg-boxdark/60'
                          }`}
                        >
                          {step.done ? (
                            <span className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full bg-success text-white">
                              <Check className="h-3.5 w-3.5" strokeWidth={3} />
                            </span>
                          ) : active ? (
                            <span className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full border-2 border-primary bg-white dark:bg-boxdark">
                              <span className="h-2 w-2 rounded-full bg-primary" />
                            </span>
                          ) : (
                            <span className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full border-2 border-stroke bg-white text-[10px] font-bold text-bodydark2 dark:border-strokedark dark:bg-boxdark">
                              {i + 1}
                            </span>
                          )}
                          <span
                            className={`line-clamp-2 min-w-0 text-[13px] leading-tight ${
                              active
                                ? 'font-semibold text-primary'
                                : step.done
                                ? 'font-medium text-black dark:text-white'
                                : 'text-bodydark2'
                            }`}
                          >
                            {content.title}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}

            <div>
              <p className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-bodydark2">
                Guía
              </p>
              <button
                type="button"
                onClick={() => setIndex(steps.length)}
                className={`flex w-full items-center gap-3 rounded-lg py-1.5 pl-2 pr-2 text-left text-[13px] font-medium transition ${
                  phase === 'usage'
                    ? 'bg-primary/10 text-primary'
                    : 'text-body hover:bg-white/70 dark:text-bodydark dark:hover:bg-boxdark/60'
                }`}
              >
                <span
                  className={`flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full ${
                    phase === 'usage'
                      ? 'bg-primary text-white'
                      : 'bg-stroke text-bodydark2 dark:bg-strokedark'
                  }`}
                >
                  <Rocket className="h-3.5 w-3.5" />
                </span>
                Cómo se usa
              </button>
            </div>
          </nav>
        </aside>

        {/* Panel principal */}
        <div className="flex min-w-0 flex-1 flex-col">
          {/* Header + progreso */}
          <div className="border-b border-stroke px-7 py-4 dark:border-strokedark">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="truncate text-lg font-bold text-black dark:text-white">
                  {phase === 'setup'
                    ? 'Configura tu restaurante'
                    : phase === 'usage'
                    ? 'Cómo se usa el módulo'
                    : '¡Todo listo!'}
                </h3>
                <p className="mt-0.5 text-xs text-bodydark2">
                  {phase === 'setup'
                    ? 'Deja lista cada pieza para empezar a operar.'
                    : phase === 'usage'
                    ? 'Un recorrido rápido por el día a día.'
                    : 'Resumen de lo que ya tienes configurado.'}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <button
                  type="button"
                  onClick={onRefresh}
                  title="Actualizar estado"
                  className="rounded-lg p-2 text-bodydark2 transition hover:bg-gray-2 hover:text-primary dark:hover:bg-meta-4"
                >
                  <RefreshCw
                    className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`}
                  />
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  title="Cerrar"
                  className="rounded-lg p-2 text-bodydark2 transition hover:bg-gray-2 hover:text-danger dark:hover:bg-meta-4"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="mt-3 flex items-center gap-3">
              <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-2 dark:bg-meta-4">
                <div
                  className="h-full rounded-full bg-primary transition-all"
                  style={{
                    width: `${Math.round(
                      ((clamped + 1) / totalScreens) * 100
                    )}%`,
                  }}
                />
              </div>
              <span className="shrink-0 text-[11px] font-medium text-bodydark2">
                {clamped + 1} / {totalScreens}
              </span>
            </div>
          </div>

          {/* Cuerpo */}
          <div className="flex-1 overflow-y-auto px-7 py-7">
            {loading && !status ? (
              <div className="flex h-full items-center justify-center">
                <Loader2 className="h-7 w-7 animate-spin text-primary" />
              </div>
            ) : setupStep ? (
              <SetupStepView
                step={setupStep}
                position={clamped + 1}
                total={steps.length}
                onNavigate={onNavigate}
              />
            ) : usageSlide ? (
              <div className="mx-auto max-w-lg py-4 text-center">
                <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                  {usageSlide.icon}
                </span>
                <p className="mt-4 text-[11px] font-semibold uppercase tracking-[0.16em] text-primary">
                  Cómo se usa · {clamped - steps.length + 1} / {usage.length}
                </p>
                <h4 className="mt-1.5 text-2xl font-bold text-black dark:text-white">
                  {usageSlide.title}
                </h4>
                <p className="mx-auto mt-3 max-w-md text-sm leading-7 text-body dark:text-bodydark">
                  {usageSlide.body}
                </p>
              </div>
            ) : (
              <div className="mx-auto max-w-lg py-4 text-center">
                <span className="mx-auto flex h-[72px] w-[72px] items-center justify-center rounded-3xl bg-success/10 text-success">
                  <Rocket className="h-9 w-9" />
                </span>
                <h4 className="mt-5 text-2xl font-bold text-black dark:text-white">
                  {status?.ready
                    ? 'Ya puedes tomar órdenes'
                    : 'Completa los pasos obligatorios cuando quieras'}
                </h4>
                <p className="mx-auto mt-3 max-w-md text-sm leading-7 text-body dark:text-bodydark">
                  {status?.ready
                    ? 'La configuración mínima está lista. Abre una mesa en Órdenes para empezar a operar. Puedes volver a esta guía desde el dashboard.'
                    : 'Puedes empezar a operar con salones, mesas y menú listos. El resto (inventario, recetas, impresoras) lo configuras cuando lo necesites.'}
                </p>
                {status && (
                  <div className="mx-auto mt-6 grid max-w-sm grid-cols-2 gap-3">
                    <div className="rounded-xl border border-stroke bg-gray-2/40 px-4 py-3 dark:border-strokedark dark:bg-meta-4/20">
                      <p className="text-2xl font-bold text-black dark:text-white">
                        {status.completedRequired}/{status.totalRequired}
                      </p>
                      <p className="text-[11px] text-bodydark2">
                        Pasos obligatorios
                      </p>
                    </div>
                    <div className="rounded-xl border border-stroke bg-gray-2/40 px-4 py-3 dark:border-strokedark dark:bg-meta-4/20">
                      <p className="text-2xl font-bold text-black dark:text-white">
                        {status.percent}%
                      </p>
                      <p className="text-[11px] text-bodydark2">
                        Configuración total
                      </p>
                    </div>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => onNavigate('/restaurant/operation/orders')}
                  className="mt-6 inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-primary/90"
                >
                  Ir a Órdenes <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between gap-3 border-t border-stroke px-6 py-4 dark:border-strokedark">
            <button
              type="button"
              onClick={goBack}
              disabled={clamped === 0}
              className="inline-flex items-center gap-2 rounded-lg border border-stroke px-4 py-2 text-sm font-medium transition hover:bg-gray-2 disabled:opacity-40 dark:border-strokedark dark:hover:bg-meta-4"
            >
              <ArrowLeft className="h-4 w-4" /> Atrás
            </button>

            {phase === 'done' ? (
              <button
                type="button"
                onClick={onFinish}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2 text-sm font-medium text-white"
              >
                <Check className="h-4 w-4" /> Terminar
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-lg px-3 py-2 text-sm font-medium text-bodydark2 transition hover:text-black dark:hover:text-white"
                >
                  Omitir por ahora
                </button>
                <button
                  type="button"
                  onClick={goNext}
                  className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2 text-sm font-medium text-white"
                >
                  {phase === 'setup' && setupStep && !setupStep.done
                    ? 'Continuar'
                    : 'Siguiente'}
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

const SetupStepView = ({
  step,
  position,
  total,
  onNavigate,
}: {
  step: RestaurantOnboardingStatus['steps'][number];
  position: number;
  total: number;
  onNavigate: (route: string) => void;
}) => {
  const content = ONBOARDING_STEP_CONTENT[step.key];

  return (
    <div className="mx-auto max-w-2xl">
      {/* Encabezado del paso */}
      <div className="flex items-start gap-4">
        <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
          {content.icon}
        </span>
        <div className="min-w-0 pt-0.5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-bodydark2">
              Paso {position} de {total}
            </span>
            <span
              className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                step.required
                  ? 'bg-primary/10 text-primary'
                  : 'bg-gray-2 text-bodydark2 dark:bg-meta-4'
              }`}
            >
              {step.required ? 'Obligatorio' : 'Opcional'}
            </span>
          </div>
          <h4 className="mt-1 text-2xl font-bold leading-tight text-black dark:text-white">
            {content.title}
          </h4>
        </div>
      </div>

      {/* Tarjeta de estado */}
      <div
        className={`mt-6 flex items-center gap-3 rounded-xl border px-4 py-3 ${
          step.done
            ? 'border-success/30 bg-success/5'
            : 'border-warning/40 bg-warning/5'
        }`}
      >
        <span
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
            step.done
              ? 'bg-success text-white'
              : 'bg-warning/20 text-meta-8 dark:text-warning'
          }`}
        >
          {step.done ? <Check className="h-5 w-5" strokeWidth={3} /> : '!'}
        </span>
        <div className="min-w-0">
          <p
            className={`text-sm font-semibold ${
              step.done ? 'text-success' : 'text-meta-8 dark:text-warning'
            }`}
          >
            {step.done ? 'Configurado' : 'Pendiente'}
          </p>
          <p className="text-xs text-bodydark2">
            {step.done
              ? step.count > 0
                ? `${step.count} registro${
                    step.count === 1 ? '' : 's'
                  } en el sistema`
                : 'Listo para operar'
              : step.required
              ? 'Necesario antes de tomar órdenes'
              : 'Puedes configurarlo más adelante'}
          </p>
        </div>
      </div>

      {/* Descripción */}
      <p className="mt-6 text-[15px] leading-7 text-body dark:text-bodydark">
        {content.description}
      </p>

      {content.hint && (
        <div className="mt-4 flex gap-3 rounded-xl border border-primary/15 bg-primary/5 px-4 py-3">
          <Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <p className="text-sm leading-6 text-body dark:text-bodydark">
            {content.hint}
          </p>
        </div>
      )}

      {/* Acción */}
      <button
        type="button"
        onClick={() => onNavigate(content.route)}
        className="mt-6 inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-primary/90"
      >
        {step.done ? 'Revisar / editar' : 'Configurar ahora'}
        <ArrowRight className="h-4 w-4" />
      </button>
    </div>
  );
};

export default RestaurantOnboardingWizard;
